/**
 * AdminDashboard Page
 * User management dashboard for administrators.
 * Displays all registered users with their activity data.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Loader2,
  Users,
  Search,
  Shield,
  ShieldAlert,
  RefreshCw,
  FileText,
  Sparkles,
  CreditCard,
  Landmark,
  Calendar,
  Clock,
  Mail,
  CheckCircle,
  XCircle,
  Trash2,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/contexts/AuthContext';
import { PageLayout } from '@/components';
import { UnauthenticatedPage } from '@/components/ui';
import { adminApi, AdminUser } from '@/services/adminApi';
import { errorMessages } from '@/lib/errors';
import { formatDate as formatDateLocale } from '@/lib/i18n-utils';

type SortField = 'user' | 'plan' | 'status' | 'documents' | 'joined' | 'lastActive';
type SortDirection = 'asc' | 'desc';

export const AdminDashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, isAdmin } = useAuthContext();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('joined');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [resettingQuotaUserId, setResettingQuotaUserId] = useState<string | null>(null);

  // Fetch users
  const fetchUsers = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    try {
      const response = await adminApi.listUsers();
      setUsers(response.users);
      setFilteredUsers(response.users);
    } catch (err) {
      setError(errorMessages.loadUsers(err));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Load users on mount
  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      fetchUsers();
    }
  }, [isAuthenticated, isAdmin, fetchUsers]);

  // Toggle sort field/direction
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter and sort users
  useEffect(() => {
    let result = [...users];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (user) =>
          user.email.toLowerCase().includes(query) ||
          (user.fullName && user.fullName.toLowerCase().includes(query)) ||
          (user.plan && user.plan.toLowerCase().includes(query))
      );
    }

    // Apply sort
    result.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'user':
          cmp = (a.fullName || a.email).localeCompare(b.fullName || b.email);
          break;
        case 'plan':
          cmp = (a.plan || 'free').localeCompare(b.plan || 'free');
          break;
        case 'status':
          cmp = (a.emailVerified ? 1 : 0) - (b.emailVerified ? 1 : 0);
          break;
        case 'documents':
          cmp = a.documentCount - b.documentCount;
          break;
        case 'joined':
          cmp = (a.createdAt || '').localeCompare(b.createdAt || '');
          break;
        case 'lastActive':
          cmp = (a.lastModified || '').localeCompare(b.lastModified || '');
          break;
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });

    setFilteredUsers(result);
  }, [searchQuery, users, sortField, sortDirection]);

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
      return formatDateLocale(dateString, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  // Handle user deletion
  const handleDeleteUser = useCallback(async () => {
    if (!userToDelete || deleteConfirmText !== 'DELETE') return;

    setDeletingUserId(userToDelete.userId);
    setDeleteError(null);

    try {
      await adminApi.deleteUser(userToDelete.userId);
      setUsers((prev) => prev.filter((u) => u.userId !== userToDelete.userId));
      setFilteredUsers((prev) => prev.filter((u) => u.userId !== userToDelete.userId));
      setUserToDelete(null);
      setDeleteConfirmText('');
    } catch (err) {
      setDeleteError(errorMessages.adminDeleteUser(err));
    } finally {
      setDeletingUserId(null);
    }
  }, [userToDelete, deleteConfirmText]);

  // Handle quota reset
  const handleResetQuota = useCallback(async (userId: string) => {
    setResettingQuotaUserId(userId);
    try {
      await adminApi.resetUserQuota(userId);
      toast.success(t('admin.dashboard.quotaResetSuccess'));
    } catch (err) {
      toast.error(errorMessages.adminResetQuota(err));
    } finally {
      setResettingQuotaUserId(null);
    }
  }, [t]);

  // Sort indicator icon
  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3.5 h-3.5 text-slate-300" />;
    return sortDirection === 'asc'
      ? <ArrowUp className="w-3.5 h-3.5 text-indigo-600" />
      : <ArrowDown className="w-3.5 h-3.5 text-indigo-600" />;
  };

  // Get plan badge color
  const getPlanBadgeColor = (plan: string | null) => {
    switch (plan?.toLowerCase()) {
      case 'starter':
        return 'bg-blue-100 text-blue-800';
      case 'pro':
        return 'bg-indigo-100 text-indigo-800';
      case 'enterprise':
      case 'organization':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-slate-100 text-slate-600';
    }
  };

  // Auth loading
  if (authLoading) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-12 sm:pt-24">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        </div>
      </PageLayout>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <UnauthenticatedPage
        icon={<Shield className="w-8 h-8 text-slate-400" />}
        title={t('admin.dashboard.title')}
        description={t('admin.dashboard.description')}
      />
    );
  }

  // Not admin
  if (!isAdmin) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-12 sm:pt-24">
          <div className="text-center py-16">
            <ShieldAlert className="w-16 h-16 mx-auto mb-4 text-red-400" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('admin.dashboard.accessDenied')}</h1>
            <p className="text-slate-600 mb-6">
              {t('admin.dashboard.noPermission')}
            </p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-600 text-white rounded-lg font-medium hover:bg-slate-700 transition-colors"
            >
              {t('admin.dashboard.goHome')}
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Loading users
  if (isLoading) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-12 sm:pt-24">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mr-3" />
            <span className="text-slate-600">{t('admin.dashboard.loadingUsers')}</span>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Error state
  if (error && users.length === 0) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-12 sm:pt-24">
          <div className="text-center py-16">
            <ShieldAlert className="w-16 h-16 mx-auto mb-4 text-red-400" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('admin.dashboard.errorLoadingUsers')}</h1>
            <p className="text-red-600 mb-6">{error}</p>
            <button
              onClick={() => fetchUsers()}
              className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              {t('admin.dashboard.tryAgain')}
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto px-6 pt-20 pb-12 sm:pt-24">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Users className="w-6 h-6 text-indigo-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">{t('admin.dashboard.title')}</h1>
            </div>
            <p className="text-slate-600">
              {t('admin.dashboard.manageUsers', { count: users.length })}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <Link
              to="/admin/templates"
              className="inline-flex items-center gap-2 px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors font-medium text-sm"
            >
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">{t('admin.dashboard.manageTemplates')}</span>
              <span className="sm:hidden">Templates</span>
            </Link>
            <Link
              to="/admin/system-prompts"
              className="inline-flex items-center gap-2 px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors font-medium text-sm"
            >
              <Sparkles className="w-4 h-4" />
              <span className="hidden sm:inline">{t('admin.dashboard.systemPrompts')}</span>
              <span className="sm:hidden">Prompts</span>
            </Link>
            <Link
              to="/admin/plans"
              className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors font-medium text-sm"
            >
              <CreditCard className="w-4 h-4" />
              <span className="hidden sm:inline">{t('admin.dashboard.managePlans')}</span>
              <span className="sm:hidden">Plans</span>
            </Link>
            <Link
              to="/admin/operating-model"
              className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors font-medium text-sm"
            >
              <Landmark className="w-4 h-4" />
              <span className="hidden sm:inline">{t('admin.dashboard.operatingModel')}</span>
              <span className="sm:hidden">Model</span>
            </Link>
            <button
              onClick={() => fetchUsers(true)}
              disabled={isRefreshing}
              className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{t('admin.dashboard.refresh')}</span>
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder={t('admin.dashboard.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
          />
        </div>

        {/* Error banner */}
        {error && users.length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Users table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {([
                    { field: 'user' as SortField, label: t('admin.dashboard.columnUser'), align: 'left' },
                    { field: 'plan' as SortField, label: t('admin.dashboard.columnPlan'), align: 'left' },
                    { field: 'status' as SortField, label: t('admin.dashboard.columnStatus'), align: 'left' },
                    { field: 'documents' as SortField, label: t('admin.dashboard.columnDocuments'), align: 'left' },
                    { field: 'joined' as SortField, label: t('admin.dashboard.columnJoined'), align: 'left' },
                    { field: 'lastActive' as SortField, label: t('admin.dashboard.columnLastActive'), align: 'left' },
                  ]).map(({ field, label }) => (
                    <th
                      key={field}
                      onClick={() => handleSort(field)}
                      className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer select-none hover:text-slate-700 transition-colors"
                    >
                      <div className="inline-flex items-center gap-1.5">
                        {label}
                        <SortIcon field={field} />
                      </div>
                    </th>
                  ))}
                  <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    {t('admin.dashboard.columnActions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                      {searchQuery ? t('admin.dashboard.noUsersSearch') : t('admin.dashboard.noUsersYet')}
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.userId} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                            <span className="text-indigo-600 font-medium">
                              {(user.fullName || user.email).charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium text-slate-900">
                              {user.fullName || '-'}
                            </div>
                            <div className="flex items-center gap-1 text-sm text-slate-500">
                              <Mail className="w-3 h-3" />
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlanBadgeColor(
                            user.plan
                          )}`}
                        >
                          {user.plan || t('admin.dashboard.planFree')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {user.emailVerified ? (
                            <>
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              <span className="text-sm text-green-700">{t('admin.dashboard.statusVerified')}</span>
                            </>
                          ) : (
                            <>
                              <XCircle className="w-4 h-4 text-amber-500" />
                              <span className="text-sm text-amber-700">{t('admin.dashboard.statusPending')}</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-slate-600">
                          <FileText className="w-4 h-4" />
                          <span>{user.documentCount}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Calendar className="w-4 h-4" />
                          {formatDate(user.createdAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Clock className="w-4 h-4" />
                          {formatDate(user.lastModified)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleResetQuota(user.userId)}
                            disabled={resettingQuotaUserId === user.userId}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-indigo-600 hover:text-white hover:bg-indigo-600 border border-indigo-200 hover:border-indigo-600 rounded-lg transition-colors disabled:opacity-50"
                            title={t('admin.dashboard.resetQuotaTitle')}
                          >
                            {resettingQuotaUserId === user.userId ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <RotateCcw className="w-3.5 h-3.5" />
                            )}
                            {t('admin.dashboard.resetQuotaButton')}
                          </button>
                          <button
                            onClick={() => {
                              setUserToDelete(user);
                              setDeleteConfirmText('');
                              setDeleteError(null);
                            }}
                            disabled={deletingUserId === user.userId}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:text-white hover:bg-red-600 border border-red-200 hover:border-red-600 rounded-lg transition-colors disabled:opacity-50"
                            title={t('admin.dashboard.deleteUserTitle')}
                          >
                            {deletingUserId === user.userId ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                            {t('admin.dashboard.deleteButton')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Stats summary */}
        {users.length > 0 && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="text-2xl font-bold text-slate-900">{users.length}</div>
              <div className="text-sm text-slate-500">{t('admin.dashboard.totalUsers')}</div>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="text-2xl font-bold text-indigo-600">
                {users.filter((u) => u.plan?.toLowerCase() === 'pro').length}
              </div>
              <div className="text-sm text-slate-500">{t('admin.dashboard.proUsers')}</div>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="text-2xl font-bold text-green-600">
                {users.filter((u) => u.emailVerified).length}
              </div>
              <div className="text-sm text-slate-500">{t('admin.dashboard.verified')}</div>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="text-2xl font-bold text-slate-600">
                {users.reduce((sum, u) => sum + u.documentCount, 0)}
              </div>
              <div className="text-sm text-slate-500">{t('admin.dashboard.totalDocuments')}</div>
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {userToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">{t('admin.dashboard.deleteModalTitle')}</h3>
            </div>

            <p className="text-slate-600 mb-2">
              {t('admin.dashboard.deleteModalDescription')}
            </p>
            <p className="font-medium text-slate-900 mb-4">
              {userToDelete.email}
              {userToDelete.fullName && (
                <span className="text-slate-500 font-normal"> ({userToDelete.fullName})</span>
              )}
            </p>

            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-700" dangerouslySetInnerHTML={{ __html: t('admin.dashboard.deleteModalWarning') }} />
            </div>

            <label className="block text-sm font-medium text-slate-700 mb-2" dangerouslySetInnerHTML={{ __html: t('admin.dashboard.deleteConfirmLabel') }} />
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={t('admin.dashboard.deleteConfirmPlaceholder')}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-4"
              autoFocus
            />

            {deleteError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{deleteError}</p>
              </div>
            )}

            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => {
                  setUserToDelete(null);
                  setDeleteConfirmText('');
                  setDeleteError(null);
                }}
                disabled={!!deletingUserId}
                className="px-4 py-2 text-sm text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                {t('admin.dashboard.cancel')}
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={deleteConfirmText !== 'DELETE' || !!deletingUserId}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingUserId ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('admin.dashboard.deleting')}
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    {t('admin.dashboard.deleteAccount')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default AdminDashboard;
